import { useState, useEffect } from 'react'

interface TimeRangeSliderProps {
  startTime: string // Formato "HH:MM"
  endTime: string // Formato "HH:MM"
  onChange: (start: string, end: string) => void
  disabled?: boolean
}

/**
 * Convierte hora "HH:MM" a minutos desde medianoche
 */
function timeToMinutes(time: string): number {
  const [hours, minutes] = time.split(':').map(Number)
  return hours * 60 + minutes
}

/**
 * Convierte minutos desde medianoche a "HH:MM"
 */
function minutesToTime(minutes: number): string {
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`
}

export default function TimeRangeSlider({ startTime, endTime, onChange, disabled = false }: TimeRangeSliderProps) {
  const [startMinutes, setStartMinutes] = useState(timeToMinutes(startTime))
  const [endMinutes, setEndMinutes] = useState(timeToMinutes(endTime))
  const [dragging, setDragging] = useState<'start' | 'end' | null>(null)

  // Actualizar cuando cambien las props
  useEffect(() => {
    setStartMinutes(timeToMinutes(startTime))
  }, [startTime])

  useEffect(() => {
    setEndMinutes(timeToMinutes(endTime))
  }, [endTime])

  const handleStartChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value)
    setStartMinutes(value)
    // No permitir que el inicio sea después del fin
    if (value >= endMinutes) {
      const newEnd = Math.min(value + 60, 1439) // Mínimo 1 hora de diferencia
      setEndMinutes(newEnd)
      onChange(minutesToTime(value), minutesToTime(newEnd))
    } else {
      onChange(minutesToTime(value), minutesToTime(endMinutes))
    }
  }

  const handleEndChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value)
    setEndMinutes(value)
    // No permitir que el fin sea antes del inicio
    if (value <= startMinutes) {
      const newStart = Math.max(value - 60, 0) // Mínimo 1 hora de diferencia
      setStartMinutes(newStart)
      onChange(minutesToTime(newStart), minutesToTime(value))
    } else {
      onChange(minutesToTime(startMinutes), minutesToTime(value))
    }
  }

  // Calcular posiciones en porcentaje para la visualización
  const startPercent = (startMinutes / 1440) * 100
  const endPercent = (endMinutes / 1440) * 100

  return (
    <div className="space-y-4">
      {/* Barra visual de 24 horas */}
      <div className="relative h-12 bg-gradient-to-r from-blue-100 via-blue-50 to-blue-100 dark:from-blue-900/20 dark:via-blue-900/10 dark:to-blue-900/20 rounded-lg border border-gray-300 dark:border-gray-600">
        {/* Marcas de horas */}
        <div className="absolute inset-0 flex justify-between px-1 py-1 text-xs text-slate-500 dark:text-slate-400">
          {[0, 6, 12, 18, 24].map((hour) => (
            <span key={hour} className="text-center" style={{ fontSize: '10px' }}>
              {hour}h
            </span>
          ))}
        </div>

        {/* Periodo INACTIVO resaltado */}
        <div
          className="absolute top-0 bottom-0 bg-red-300 dark:bg-red-800/50 border-l-2 border-r-2 border-red-500 dark:border-red-600 transition-all"
          style={{
            left: `${startPercent}%`,
            right: `${100 - endPercent}%`,
          }}
        >
          <div className="absolute inset-0 flex items-center justify-center text-xs font-semibold text-red-900 dark:text-red-100">
            {!disabled && 'INACTIVO'}
          </div>
        </div>

        {/* Marcador de inicio */}
        <div
          className="absolute top-0 bottom-0 w-1 bg-red-600 dark:bg-red-500 cursor-ew-resize"
          style={{ left: `${startPercent}%` }}
          onMouseDown={() => !disabled && setDragging('start')}
        >
          <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-red-600 dark:bg-red-500 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
            {minutesToTime(startMinutes)}
          </div>
        </div>

        {/* Marcador de fin */}
        <div
          className="absolute top-0 bottom-0 w-1 bg-red-600 dark:bg-red-500 cursor-ew-resize"
          style={{ left: `${endPercent}%` }}
          onMouseDown={() => !disabled && setDragging('end')}
        >
          <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-red-600 dark:bg-red-500 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
            {minutesToTime(endMinutes)}
          </div>
        </div>
      </div>

      {/* Controles de rango con sliders */}
      <div className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Inicio periodo inactivo
          </label>
          <div className="flex items-center gap-3">
            <input
              type="range"
              min="0"
              max="1439"
              step="15"
              value={startMinutes}
              onChange={handleStartChange}
              disabled={disabled}
              className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <span className="text-sm font-mono text-slate-900 dark:text-white w-16 text-right">
              {minutesToTime(startMinutes)}
            </span>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Fin periodo inactivo
          </label>
          <div className="flex items-center gap-3">
            <input
              type="range"
              min="0"
              max="1439"
              step="15"
              value={endMinutes}
              onChange={handleEndChange}
              disabled={disabled}
              className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <span className="text-sm font-mono text-slate-900 dark:text-white w-16 text-right">
              {minutesToTime(endMinutes)}
            </span>
          </div>
        </div>
      </div>

      {/* Inputs precisos de tiempo */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs text-slate-600 dark:text-slate-400 mb-1">
            Hora inicio (HH:MM)
          </label>
          <input
            type="time"
            value={minutesToTime(startMinutes)}
            onChange={(e) => {
              const minutes = timeToMinutes(e.target.value)
              setStartMinutes(minutes)
              if (minutes >= endMinutes) {
                const newEnd = Math.min(minutes + 60, 1439)
                setEndMinutes(newEnd)
                onChange(e.target.value, minutesToTime(newEnd))
              } else {
                onChange(e.target.value, minutesToTime(endMinutes))
              }
            }}
            disabled={disabled}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          />
        </div>

        <div>
          <label className="block text-xs text-slate-600 dark:text-slate-400 mb-1">
            Hora fin (HH:MM)
          </label>
          <input
            type="time"
            value={minutesToTime(endMinutes)}
            onChange={(e) => {
              const minutes = timeToMinutes(e.target.value)
              setEndMinutes(minutes)
              if (minutes <= startMinutes) {
                const newStart = Math.max(minutes - 60, 0)
                setStartMinutes(newStart)
                onChange(minutesToTime(newStart), e.target.value)
              } else {
                onChange(minutesToTime(startMinutes), e.target.value)
              }
            }}
            disabled={disabled}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          />
        </div>
      </div>

      <p className="text-xs text-slate-500 dark:text-slate-400 text-center">
        El transportista estará <span className="font-semibold text-red-600 dark:text-red-400">INACTIVO</span> durante este periodo.
        Fuera de este horario, el transportista estará activo para pedidos del día siguiente.
      </p>
    </div>
  )
}
